import * as THREE from "three";
import { easeCubic, LEG_HEIGHT_TWEEN_MS } from "./tweenTiming.js";

// Leg-height rig; installed on FurnitureElement (statics) and its prototype.
export const legHeightStatics = {
  /**
   * Pure math helper. baseCm = the model's authored leg height; targetCm = the
   * height the user picked. Returns { scaleY, bodyDeltaMeters }.
   *   scaleY           — multiplier applied to the leg group on its world-up axis
   *   bodyDeltaMeters  — how far mesh.position.y lifts (positive when target > base)
   */
  computeLegTransform(baseCm, targetCm) {
    const base = Number(baseCm);
    const target = Number(targetCm);
    return { scaleY: target / base, bodyDeltaMeters: (target - base) / 100 };
  },
};

export const legHeightMethods = {
  /**
   * Lazy resolve leg groups + snapshot bind LOCAL pose. Called from applyLegHeight
   * on first use. Returns true if at least one leg group resolved.
   *
   * Snapshot is parent-LOCAL (not world): world position depends on
   * mesh.position, which the user can drag. Per-apply counter-translation is
   * recomputed in applyLegHeight from the parent's current world matrix.
   */
  _ensureLegGroups() {
    if (this._legGroups !== null) return this._legGroups.length > 0;
    const legProps = this.furnitureData?.properties?.legProperties;
    if (!legProps || !this.mesh) { this._legGroups = []; return false; }

    const names = legProps.groupNames || (legProps.groupName ? [legProps.groupName] : []);
    const resolved = [];
    for (const name of names) {
      const node = this.mesh.getObjectByName(name);
      if (!node) {
        console.warn(
          `[legHeight] node '${name}' not found on ${this.furnitureData.urlCode}; skipping`
        );
        continue;
      }
      resolved.push(node);
    }
    if (resolved.length === 0) { this._legGroups = []; return false; }

    // Determine which local axis of the leg group aligns with world-up. For
    // models authored Blender Z-up and exported with a 90° X swap (clooods,
    // cascais) this is local-Z; for unrotated diagon legs it's local-Y. All
    // groups in legProperties.groupNames share the same parent so we sample
    // once.
    this.mesh.updateMatrixWorld(true);
    const tmpQuat = new THREE.Quaternion();
    resolved[0].parent.getWorldQuaternion(tmpQuat);
    tmpQuat.invert();
    const localUp = new THREE.Vector3(0, 1, 0).applyQuaternion(tmpQuat);
    const ax = Math.abs(localUp.x), ay = Math.abs(localUp.y), az = Math.abs(localUp.z);
    this._legScaleAxis = ax >= ay && ax >= az ? 'x' : ay >= az ? 'y' : 'z';

    this._legGroups = resolved.map((group) => ({
      group,
      bindLocalPos: group.position.clone(),
      bindScaleAxisValue: group.scale[this._legScaleAxis],
    }));
    return true;
  },

  /**
   * Apply the current leg height to this element. Scales the leg group(s) along
   * their world-up local axis (so the leg bottom stays anchored on the floor),
   * and lifts mesh.position.y by (target - base) cm so the body floats up/down
   * to follow the new leg top. Attached components reparented under this mesh
   * (panels, cushions, backrests) lift automatically via matrixWorld; sibling
   * modules (module-to-module attachments) are handled separately by callers
   * iterating over every leg-bearing element.
   *
   * opts:
   *   animate          — true to tween via the variant-tween path (~250ms cubic-in-out)
   *   durationMs       — override the tween duration
   *   assumeUnlifted   — true (default) when mesh.position.y is at its logical
   *                      (un-lifted) value. Pass false on state restore where
   *                      the saved Y already has the lift baked in.
   */
  applyLegHeight(targetCm, opts = {}) {
    const legProps = this.furnitureData?.properties?.legProperties;
    if (!legProps) return;
    if (!this._ensureLegGroups()) return;

    const target = parseFloat(targetCm);
    if (!Number.isFinite(target) || target <= 0) return;
    const { scaleY, bodyDeltaMeters } = legHeightStatics.computeLegTransform(legProps.baseHeight, target);

    const animate = !!opts.animate;
    const assumeUnlifted = opts.assumeUnlifted !== false;
    const axis = this._legScaleAxis;
    const oldLift = this._legBodyLiftY || 0;

    // Recompute parent rotation+scale inverse each call. The user may have
    // rotated the mesh since the last apply; the legGroup parent's rotation
    // changes accordingly. (Translation doesn't affect the linear part.)
    const legParent = this._legGroups[0].group.parent;
    legParent.updateMatrixWorld(true);
    const linearInv = new THREE.Matrix4().copy(legParent.matrixWorld);
    linearInv.setPosition(0, 0, 0);
    linearInv.invert();

    // We need the leg group to translate by (0, -bodyDeltaMeters, 0) in world
    // space so the leg bottom stays at world Y=0 even though mesh.position.y
    // lifts by bodyDeltaMeters. Convert the world delta into the leg group's
    // parent-local frame.
    const localDelta = new THREE.Vector3(0, -bodyDeltaMeters, 0).applyMatrix4(linearInv);

    if (!animate) {
      this._legHeightTween = null;
      if (assumeUnlifted) {
        this.mesh.position.y += (bodyDeltaMeters - oldLift);
      }
      for (const lg of this._legGroups) {
        lg.group.scale[axis] = lg.bindScaleAxisValue * scaleY;
        lg.group.position.copy(lg.bindLocalPos).add(localDelta);
      }
      this._legBodyLiftY = bodyDeltaMeters;
      this.computeBoundingBoxFull();
      return;
    }

    const fromMeshY = this.mesh.position.y;
    const toMeshY = assumeUnlifted ? (fromMeshY - oldLift + bodyDeltaMeters) : fromMeshY;
    const groups = this._legGroups.map((lg) => ({
      group: lg.group,
      fromPos: lg.group.position.clone(),
      toPos: lg.bindLocalPos.clone().add(localDelta),
      fromScale: lg.group.scale[axis],
      toScale: lg.bindScaleAxisValue * scaleY,
    }));

    this._legHeightTween = {
      elapsed: 0,
      duration: (opts.durationMs ?? LEG_HEIGHT_TWEEN_MS) / 1000,
      axis,
      groups,
      fromMeshY,
      toMeshY,
      meshTweenActive: assumeUnlifted,
      targetLift: bodyDeltaMeters,
    };
  },

  /**
   * The logical (un-lifted) Y position to persist. State save uses this so the
   * stored layout encodes the user-placed Y, not the visual lift.
   */
  getLogicalY() {
    if (!this.mesh) return 0;
    return this.mesh.position.y - (this._legBodyLiftY || 0);
  },

  _updateLegHeightTween(deltaTime) {
    const lh = this._legHeightTween;
    if (!lh) return;
    lh.elapsed += deltaTime;
    const t = Math.min(lh.elapsed / lh.duration, 1);
    const ease = easeCubic(t);

    if (lh.meshTweenActive) {
      this.mesh.position.y = lh.fromMeshY + (lh.toMeshY - lh.fromMeshY) * ease;
      // Keep AABB in sync per-frame so concurrent drags / collision checks see
      // the tweening Y.
      this.updateBoundingBoxPosition();
    }
    for (const g of lh.groups) {
      g.group.position.lerpVectors(g.fromPos, g.toPos, ease);
      g.group.scale[lh.axis] = g.fromScale + (g.toScale - g.fromScale) * ease;
    }

    if (t >= 1) {
      this._legBodyLiftY = lh.targetLift;
      this.computeBoundingBoxFull();
      this._legHeightTween = null;
    }
  },
};
